import { useState, useCallback } from "react";
import { useMutation } from "react-query";
import BigPopupTemplate from "components/templates/modal/big/BigPopupTemplate";
import { ModalInput } from "components/atoms/input/modal/ModalInput";
import Input from "components/atoms/input/Input";
import BigButton from "components/atoms/buttons/BigButton";
import * as s from "./ViewSupportStyled";
import { CenterlineText, UnderlineText } from "components/atoms/text/Text";
import { COLORS } from "styles/colors";
import { iAmAOptions, techStatusOptions } from "utils/options";
import TextArea from "components/atoms/input/TextArea";
import PhoneInput from "components/atoms/input/phone/PhoneInput";
import { TechSupportDto } from "api/interfaces/helpInterface.interface";
import { updateTechSupport } from "api/helpAPI";
import { downloadFromUrl, isVideoFile, splitFullName } from "utils/functions";
import SelectComponent from "components/atoms/select/SelectComponent";
import { DownloadButton } from "components/atoms/buttons/DownloadButton";
import { SimpleFileViewerModal } from "components/templates/modal/simpleFileViewer/SimpleFileViewerModal";
import { WithTooltip } from "components/blocks/WithTooltip";
import useApiError from "hook/useApiError";

interface Props {
  isEdit?: boolean; // 편집 모드일 때
  onModal: (type?: string) => void;
  selectedData: TechSupportDto;
  onClickSave: (videoDto: any, update?: boolean) => void;
  onClickDeactive?: (userId: string) => void;
}

export enum UPDATE_TYPE {
  CATEGORY,
  GROUP,
  TITLE,
  VIDEO_URL,
  ORDER,
  DESC,
}

function ViewSupport(props: Props): JSX.Element {
  const [editInfo, setEditInfo] = useState<TechSupportDto>(props.selectedData);
  const [isViewer, setIsViewer] = useState<boolean>(false);
  const { lang, notifyError, notifySuccess } = useApiError();

  const contactInfo = [
    {
      label: "Email",
      placeholder: "Please enter an email",
      value: props.selectedData?.email,
    },
    {
      label: "I Am A(n)",
      input: (
        <SelectComponent
          options={iAmAOptions}
          disabled
          value={editInfo.userType}
        />
      ),
    },
    {
      label: "First Name",
      placeholder: "Please enter a first name",
      maxLength: 100,
      value: splitFullName(editInfo.name),
    },
    {
      label: "Last Name",
      placeholder: "Please enter a last name",
      value: splitFullName(editInfo.name, true),
      maxLength: 100,
    },
    {
      label: "Phone Number",
      input: <PhoneInput disabled={true} selected={editInfo.phone} />,
    },
  ];

  const issueContactInfo = [
    {
      label: "Email",
      value: props.selectedData?.email,
    },
    {
      label: "Organization",
      value: props.selectedData?.accountNumber,
    },
    {
      label: "Name",
      value: editInfo.name,
    },
    {
      label: "Phone Number",
      input: <PhoneInput disabled={true} selected={editInfo.phone} />,
    },
  ];

  const onChangeProcess = useCallback(
    (treatment: string) => {
      setEditInfo({
        ...editInfo,
        treatment: treatment,
      });
    },
    [editInfo]
  );

  const productInfo = [
    {
      label: "Address",
      input: <Input disabled value={editInfo.address} />,
    },
    {
      label: "Problem Description",
      input: (
        <TextArea
          placeholder="Tell us what's wrong"
          disabled
          value={editInfo.description}
        />
      ),
    },
    {
      label: "",
      input: (
        <TextArea
          placeholder="Enter Process Result"
          value={editInfo.treatment}
          onChange={(e) => onChangeProcess(e.currentTarget.value)}
          data-testid="manager-trouble-manager-edit-textarea-change-process"
        />
      ),
    },
  ];

  const mutationUpdateTech = useMutation(updateTechSupport, {
    onSuccess: () => {
      notifySuccess(lang.label.manager.tech.modify.success);
      props.onModal();
    },
    onError: () => {
      notifyError(lang.label.manager.tech.modify.fail);
    },
  });
  const onSaveBefore = useCallback(() => {
    const formData = new FormData();
    const json = JSON.stringify(editInfo);
    const blob = new Blob([json], { type: "application/json" });
    formData.append("techSupportDto", blob);
    mutationUpdateTech.mutate({
      id: editInfo.id,
      payload: formData,
    });
  }, [editInfo, mutationUpdateTech]);

  const onClickAttachDownload = useCallback((editInfo: TechSupportDto) => {
    downloadFromUrl(editInfo.fileUrl, editInfo.fileName);
  }, []);

  const onChangeStatus = useCallback(
    (status: string) => {
      setEditInfo({
        ...editInfo,
        status: status,
      });
    },
    [editInfo]
  );

  return (
    <BigPopupTemplate
      title={props.isEdit ? "Edit Tech Support" : "Add Tech Support"}
      onModal={props.onModal}
    >
      <s.FormItem>
        <s.SubTitle>
          <CenterlineText color={COLORS.BUTTONS1} fontSize={20} bold>
            Your Contact Information
          </CenterlineText>
        </s.SubTitle>

        <s.FormWrapper>
          {editInfo.category === "issueReport"
            ? issueContactInfo.map((item, index) => (
                <ModalInput label={item.label} key={index}>
                  {item.input ? (
                    item.input
                  ) : (
                    <Input disabled value={item.value} />
                  )}
                </ModalInput>
              ))
            : contactInfo.map((item, index) => (
                <ModalInput label={item.label} key={index}>
                  {item.input ? (
                    item.input
                  ) : (
                    <Input
                      disabled
                      value={item.value}
                      maxLength={item.maxLength}
                    />
                  )}
                </ModalInput>
              ))}
        </s.FormWrapper>
        <ModalInput
          label={productInfo[0].label}
          key={"address_problem_description"}
        >
          {productInfo[0].input}
        </ModalInput>
        <ModalInput
          label={productInfo[1].label}
          key={"textarea_problem_description"}
        >
          {productInfo[1].input}
        </ModalInput>
        {editInfo.category === "issueReport" && editInfo.fileName !== "" && (
          <ModalInput label={"Attachment"} key={"attach_problem_description"}>
            <UnderlineText
              fontSize={14}
              onClick={() => onClickAttachDownload(editInfo)}
              className="web-app-link"
              data-testid="manager-trouble-manager-edit-underline-text-click-attach-download"
            >
              {props.selectedData.fileName}
            </UnderlineText>
          </ModalInput>
        )}

        {editInfo.category === "techSupport" && editInfo.fileName !== "" && (
          <ModalInput label={"Attachment"} key={"attach_problem_description"}>
            <s.AttachmentInputWrapper>
              <WithTooltip tooltip="Open">
                <UnderlineText
                  fontSize={14}
                  onClick={() => setIsViewer(true)}
                  className="web-app-link"
                  data-testid="manager-trouble-manager-edit-underline-text-set-is-viewer"
                >
                  {props.selectedData.fileName}
                </UnderlineText>
              </WithTooltip>
              <DownloadButton
                fileName={props.selectedData.fileName}
                fileUrl={editInfo.fileUrl}
              />
            </s.AttachmentInputWrapper>
          </ModalInput>
        )}

        <s.SubTitle2>
          <CenterlineText color={COLORS.BUTTONS1} fontSize={20} bold>
            Process Result
          </CenterlineText>
          <div style={{ width: "40%" }}>
            <SelectComponent
              options={techStatusOptions}
              value={techStatusOptions.find(
                (option) => option.value === editInfo.status
              )}
              onChange={onChangeStatus}
              data-testid="manager-trouble-manager-edit-selects-change-status"
            />
          </div>
        </s.SubTitle2>
        <ModalInput
          label={productInfo[2].label}
          key={"textarea_process_result"}
        >
          {productInfo[2].input}
        </ModalInput>
      </s.FormItem>

      <s.ButtonWrapper>
        <BigButton
          label="Save"
          onClickBtn={onSaveBefore}
          data-testid="manager-trouble-manager-edit-button-save-before"
        />
      </s.ButtonWrapper>
      {isViewer && (
        <SimpleFileViewerModal
          fileName={props.selectedData.fileName}
          fileUrl={props.selectedData.fileUrl}
          fileType={
            isVideoFile(props.selectedData.fileName) ? "video" : "image"
          }
          onModal={() => {
            setIsViewer(false);
          }}
        />
      )}
    </BigPopupTemplate>
  );
}

export default ViewSupport;
